/**
 * AWS WAF CAPTCHA, applied at submit rather than on arrival. (BL-0096)
 *
 * THE SHAPE. Two WAF rules sit in front of `POST /++api++/@contact`, both
 * scoped to the three archive hostnames:
 *
 *   Challenge  — silent. The SDK loaded here acquires an `aws-waf-token` in
 *                the background while the visitor is typing, so a real person
 *                in a real browser never sees anything at all. A script, a
 *                curl, or anything that cannot run JavaScript is terminated
 *                at the ALB and never reaches Plone.
 *   Captcha    — rate-based, and only then. A repeat submitter meets a puzzle;
 *                a first-time reporter does not.
 *
 * WHY NOT A PUZZLE ON PAGE LOAD. Because it charges every honest person for
 * the behaviour of a machine, and because a puzzle solved before the form is
 * filled has expired by the time it is submitted.
 *
 * THE 405 CONTRACT. AWS's documented conditional pattern is: make the request,
 * and if it comes back 405, render a puzzle and retry. That is why
 * `services/contact.py` may never answer 405 for a reason of its own — it
 * would put a puzzle in front of a visitor at a moment no puzzle can clear.
 * There is a test for it.
 *
 * SAME ORIGIN IS LOAD-BEARING. The 405 carries no CORS headers and
 * `x-amzn-waf-action` is unreadable cross-origin, so this only works because
 * the page and the API share a hostname (seamless mode). If the API ever
 * moves to its own host, this file breaks silently — the form would look like
 * it was failing rather than asking for a puzzle.
 *
 * DEGRADATION. Every step falls back rather than throwing: if the SDK cannot
 * load (blocked, offline, third-party-script blocker), the submit still goes
 * out through plain `fetch`. It will then be stopped by the Challenge rule,
 * and the component says so and offers the email address.
 */
import {
  CONTACT_ENDPOINT,
  WAF_CAPTCHA_API_KEY,
  WAF_CAPTCHA_SCRIPT,
} from '../../config/waf';

let loadPromise = null;

/** Inject the SDK once per page. Resolves true when `window.AwsWafIntegration`
 *  is usable, false when it is not — never rejects. */
export function loadWafSdk() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(false);
  }
  if (window.AwsWafIntegration && window.AwsWafCaptcha) {
    return Promise.resolve(true);
  }
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve) => {
    const existing = document.querySelector(
      `script[src="${WAF_CAPTCHA_SCRIPT}"]`,
    );
    const script = existing || document.createElement('script');
    const done = () => resolve(Boolean(window.AwsWafIntegration));
    script.addEventListener('load', done);
    script.addEventListener('error', () => resolve(false));
    if (!existing) {
      script.src = WAF_CAPTCHA_SCRIPT;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
  return loadPromise;
}

/**
 * Get a token in the background, while the visitor is still filling the form.
 * This is what makes the Challenge rule invisible: by the time they press
 * Send, the cookie is already there.
 */
export async function prefetchToken() {
  const ready = await loadWafSdk();
  if (!ready) return false;
  try {
    await window.AwsWafIntegration.getToken();
    return true;
  } catch {
    return false;
  }
}

/**
 * POST the submission. Returns the raw Response so the caller can read the
 * status — 405 is a signal here, not an error.
 */
export async function submitContact(payload) {
  const options = {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  };
  const wafFetch = window.AwsWafIntegration && window.AwsWafIntegration.fetch;
  if (wafFetch) {
    return wafFetch.call(window.AwsWafIntegration, CONTACT_ENDPOINT, options);
  }
  return window.fetch(CONTACT_ENDPOINT, options);
}

/**
 * Draw the puzzle into a container INSIDE our own page. Not a redirect, not a
 * new window: the form the visitor has already filled stays on screen and
 * intact behind it, and solving simply resumes the submit they already made.
 *
 * Resolves true when solved, false when the SDK is unavailable or the visitor
 * gives up.
 */
export function renderPuzzle(container) {
  return new Promise((resolve) => {
    if (!container || !window.AwsWafCaptcha) {
      resolve(false);
      return;
    }
    try {
      window.AwsWafCaptcha.renderCaptcha(container, {
        apiKey: WAF_CAPTCHA_API_KEY,
        onSuccess: () => resolve(true),
        onError: () => resolve(false),
      });
    } catch {
      resolve(false);
    }
  });
}
